import { Link, useNavigate } from "react-router-dom";
import { Heart, Star, Store } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useI18n, TranslatedText } from "@/context/I18nContext";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";

export const ProductCard = ({ product, index = 0 }) => {
  const { addToCart, toggleWishlist, wishlistIds } = useCart();
  const { user } = useAuth();
  const { t } = useI18n();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const price = product.discount_price || product.price;
  const hasDiscount = !!product.discount_price;
  const inWish = wishlistIds?.includes(product.product_id);

  const handleCart = async (e) => {
    e.preventDefault();
    if (!user) return navigate("/login");
    await addToCart(product.product_id, 1);
    toast.success("Added to cart");
  };
  const handleWish = async (e) => {
    e.preventDefault();
    if (!user) return navigate("/login");
    await toggleWishlist(product.product_id);
  };

  return (
    <Link to={`/product/${product.product_id}`} data-testid={`product-card-${product.product_id}`}
      className="animate-fade-up" style={{ animationDelay: `${(index % 10) * 40}ms` }}>
      <Card className="group overflow-hidden border-border/50 hover:shadow-lg hover:border-primary/30 transition-all duration-200 h-full flex flex-col">
        <div className="relative aspect-square overflow-hidden bg-muted">
          <img src={product.images?.[0]} alt={product.title} loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          {product.flash_sale && <Badge className="absolute top-2 left-2 bg-destructive text-white">⚡ Flash</Badge>}
          {hasDiscount && <Badge className="absolute top-2 right-2 bg-primary text-white">
            -{Math.round((1 - price / product.price) * 100)}%</Badge>}
          <Button data-testid={`wishlist-btn-${product.product_id}`} onClick={handleWish} size="icon" variant="secondary"
            className="absolute bottom-2 right-2 h-8 w-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
            <Heart className={`w-4 h-4 ${inWish ? "fill-primary text-primary" : ""}`} />
          </Button>
          {product.category === "wine-alcohol" && (
            <Badge data-testid={`alcohol-badge-${product.product_id}`} className="absolute bottom-2 left-2 bg-amber-600 hover:bg-amber-600 text-white gap-1 text-[10px] px-1.5">
              18+ ID required
            </Badge>
          )}
        </div>
        <div className="p-3 flex flex-col gap-1.5 flex-1">
          <TranslatedText as="h3" text={product.title} className="text-sm font-medium line-clamp-2 leading-snug flex-1" />
          {product.seller_id && (
            <button
              data-testid={`product-store-link-${product.product_id}`}
              onClick={(e) => { e.preventDefault(); navigate(`/store/${product.seller_id}`); }}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors w-fit"
            >
              <Store className="w-3 h-3" />
              <span className="line-clamp-1 max-w-[140px]">{product.seller_name || "Visit store"}</span>
            </button>
          )}
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Star className="w-3 h-3 fill-gold text-gold" />
            <span className="font-semibold text-foreground">{product.rating}</span>
            <span>· {product.sold} {t("sold")} · {product.country}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-lg font-bold text-primary font-display">{format(price)}</span>
            {hasDiscount && <span className="text-xs text-muted-foreground line-through">{format(product.price)}</span>}
          </div>
          <Button data-testid={`add-cart-btn-${product.product_id}`} onClick={handleCart} size="sm"
            className="w-full rounded-full mt-1 transition-all active:scale-95">{t("addToCart")}</Button>
        </div>
      </Card>
    </Link>
  );
};
